import React, { useContext, useEffect, useRef, useState } from "react";
import {
    StyledIcon,
    TimesheetCellProjectName,
    TimesheetClickableWrapper,
    TimesheetClientName,
    TimesheetGroupRow,
    TimesheetProjectNameWrapper,
    TimesheetRow,
} from "features/timesheet/timesheet-grid/TimesheetGrid.styles";
import {
    TimesheetProjectRowExpansionContextActions,
    TimesheetProjectRowExpansionContextValue,
} from "features/timesheet/contexts/TimesheetProjectRowExpansionContext";

import { COLOR_VALUES } from "shared/config/colors";
import Cell from "features/timesheet/timesheet-grid/Cell";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dotdotdot from "react-dotdotdot";
import DrawerContext from "shared/contexts/DrawerContext";
import HStack from "shared/components/layout/stack/HStack";
import JobsDivider from "features/timesheet/timesheet-grid/JobsDivider";
import MasterDrawer from "shared/components/data-display/drawers/MasterDrawer";
import ProjectDrawer from "features/timesheet/timesheet-grid/ProjectDrawer";
import PropTypes from "prop-types";
import QuaternaryButton from "shared/components/inputs/button/QuaternaryButton";
import SubmitTimesheetContext from "features/timesheet/contexts/SubmitTimesheetContext";
import { TIME_OFF_ROW_ID } from "features/timesheet/timesheet-grid/Grid";
import TimesheetGridContext from "features/timesheet/contexts/TimesheetGridContext";
import VStack from "shared/components/layout/stack/VStack";
import useSmallBreakpoint from "shared/hooks/ui/useSmallBreakpoint";

const MemoizedProjectCell = React.memo(Cell);

const Row = ({ hoursData, data, projectHours, oooReasonsData }) => {
    const isSmallScreen = useSmallBreakpoint();

    const { toggleProjectRow, toggleProjectRowAfterSubmit } = useContext(
        TimesheetProjectRowExpansionContextActions
    );

    const { isProjectRowOpen } = useContext(
        TimesheetProjectRowExpansionContextValue
    );

    const { selectedDateTimesheetStatus, hoursUpdateInProgress } = useContext(
        SubmitTimesheetContext
    );

    const { disableGridUpdate } = useContext(TimesheetGridContext);

    const [projectIdsOfTimesheetApproved, setProjectIdsOfTimesheetApproved] =
        useState([]);

    const [projectIdsOfTimesheetRejected, setProjectIdsOfTimesheetRejected] =
        useState([]);

    const [expandedNotRecentJobs, setExpandedNotRecentJobs] = useState({});

    const { setShow } = useContext(DrawerContext);

    const sidebarProjectId = useRef();

    const hoursDataList = hoursData?.data;
    const oooReasonsList = oooReasonsData?.data;

    useEffect(() => {
        const hoursApproved = [];
        const hoursRejected = [];
        hoursDataList?.forEach((x) => {
            if (x?.teApproved) {
                if (!hoursApproved.includes(x.jobId)) {
                    hoursApproved.push(x.jobId);
                }
            }
            if (x?.teRejected) {
                if (!hoursRejected.includes(x.jobId)) {
                    hoursRejected.push(x.jobId);
                }
            }
        });
        setProjectIdsOfTimesheetApproved(hoursApproved);
        setProjectIdsOfTimesheetRejected(hoursRejected);
    }, [hoursDataList, selectedDateTimesheetStatus]);

    const expanded = isProjectRowOpen(data.clientData.id);

    // collapse row for all empty hours cell when timesheet submitted
    useEffect(() => {
        const holidayExist =
            data?.details?.filter((d) => d?.hasOwnProperty("HalfDay")).length >
            0;

        if (selectedDateTimesheetStatus && !holidayExist) {
            const projectIdList = data?.details?.map(
                (d) => d?.ProjectId || d?.LegacyJobId
            );

            let projectIdElements = [];

            for (const element of projectIdList) {
                let total = 0;
                hoursDataList
                    ?.filter((d) => d.jobId === element)
                    ?.map((d) => (total = total + d.timeSpan));
                !total && projectIdElements.push(element);
            }

            if (projectIdElements.length === projectIdList.length) {
                toggleProjectRowAfterSubmit(data?.clientData.id);
            }
        }

        if (data?.isTimeOffRow) {
            const isTimeOffExists =
                hoursDataList?.filter((f) =>
                    oooReasonsList?.some(
                        (s) =>
                            f.timeSpan > 0 &&
                            (s.LegacyJobId === f.jobId ||
                                s.ReasonId === f.jobId)
                    )
                )?.length > 0;

            if (!holidayExist && !isTimeOffExists) {
                toggleProjectRowAfterSubmit(TIME_OFF_ROW_ID);
            }
        }
    }, [
        data,
        hoursDataList,
        oooReasonsList,
        selectedDateTimesheetStatus,
        toggleProjectRowAfterSubmit,
    ]);

    const handleClick = (projectId) => {
        // Prevent sidebar from opening on Small breakpoint (799px and below)
        if (isSmallScreen) {
            return;
        }

        setShow(true);
        if (Number.parseInt(projectId) > 1) {
            sidebarProjectId.current = projectId;
        }
    };

    const toggleNotRecentJobs = (clientId) => {
        setExpandedNotRecentJobs((prev) => ({
            ...prev,
            [clientId]: !prev[clientId],
        }));
    };

    // Separate projects by IsUsedRecently flag
    const recentJobs =
        data?.details.filter((project) => project.IsUsedRecently) || [];
    const notRecentJobs =
        data?.details.filter((project) => !project.IsUsedRecently) || [];
    const isNotRecentExpanded =
        expandedNotRecentJobs[data.clientData.id] || false;

    const renderProjectRow = (project, projectIndex) => (
        <TimesheetRow
            key={projectIndex}
            spacing="Zero"
            disabledTimeOffRow={
                (disableGridUpdate || hoursUpdateInProgress) &&
                data?.isTimeOffRow
            }
        >
            <TimesheetProjectNameWrapper
                spacing="Zero_25"
                align="center"
                justify="space-between"
            >
                <TimesheetClickableWrapper
                    onClick={(_e) => handleClick(project?.ProjectId)}
                >
                    {isSmallScreen ? (
                        <TimesheetCellProjectName>
                            {project?.hasOwnProperty("isHoliday") &&
                                project.Name}
                            {project?.ProjectName || project?.Label}
                        </TimesheetCellProjectName>
                    ) : (
                        <Dotdotdot clamp={1}>
                            <TimesheetCellProjectName>
                                {project?.hasOwnProperty("isHoliday") &&
                                    project.Name}
                                {project?.ProjectName || project?.Label}
                            </TimesheetCellProjectName>
                        </Dotdotdot>
                    )}
                </TimesheetClickableWrapper>

                {projectIdsOfTimesheetApproved.includes(project.ProjectId) && (
                    <StyledIcon name="success" color="Green" />
                )}
                {projectIdsOfTimesheetRejected.includes(project.ProjectId) && (
                    <StyledIcon name="warning" color="Orange_1" />
                )}
            </TimesheetProjectNameWrapper>

            {projectHours
                .find((x) => {
                    const id =
                        project?.ProjectId ||
                        project?.LegacyJobId ||
                        project?.Id;
                    // if it's a time off we need to find the one that match the reason and the job
                    if (x.isTimeOff && !x.isHoliday) {
                        return (
                            x.projectId === id &&
                            project.ReasonId === x.ReasonId
                        );
                    }

                    return x.projectId === id;
                })
                ?.hours?.map((cellData) => (
                    <MemoizedProjectCell
                        key={cellData.timeDay ?? cellData.Date}
                        data={cellData}
                        projectData={project}
                        projectId={
                            cellData.hasOwnProperty("jobId")
                                ? cellData.jobId
                                : project?.ProjectId ||
                                  project?.LegacyJobId ||
                                  project?.Id
                        }
                        vacationRequestId={cellData.oooVacationRequestId}
                        isTimeOff={
                            project?.LegacyJobId ||
                            cellData?.oooVacationRequestId ||
                            project?.VacationRequestId
                        }
                        readOnlyCell={
                            !selectedDateTimesheetStatus && cellData.reandOnly
                        }
                        tooltipText={cellData.tooltipText}
                        isHolidayData={
                            cellData?.hasOwnProperty("HalfDay") ||
                            project?.hasOwnProperty("HalfDay")
                        }
                        reasonId={project.ReasonId}
                        projectHours={projectHours}
                    />
                ))}
        </TimesheetRow>
    );

    return (
        <>
            {sidebarProjectId.current && !isSmallScreen && (
                <MasterDrawer stickyAction={true}>
                    <ProjectDrawer projectId={sidebarProjectId.current} />
                </MasterDrawer>
            )}
            <TimesheetGroupRow spacing="Zero_5" align="flex-start">
                <QuaternaryButton
                    leadingIcon={expanded ? "caret-down" : "caret-right"}
                    {...{
                        "aria-label": expanded ? "Collapse" : "Expand",
                    }}
                    onClick={() => toggleProjectRow(data?.clientData.id)}
                />

                <VStack spacing="Zero" style={{ width: "100%" }}>
                    <TimesheetRow isClientRow>
                        <TimesheetClientName variant="Body_1_1" color="Black">
                            <HStack spacing="One">
                                {data.clientData.name}
                                {disableGridUpdate && data?.isTimeOffRow && (
                                    <CircularProgress
                                        style={{
                                            color: COLOR_VALUES.Gray_2,
                                            height: "12px",
                                            width: "12px",
                                        }}
                                    />
                                )}
                            </HStack>
                        </TimesheetClientName>
                    </TimesheetRow>
                    {expanded && (
                        <VStack spacing="Zero">
                            {/* Always render jobs with IsUsedRecently = true first (recently used jobs) */}
                            {recentJobs.map((project, projectIndex) =>
                                renderProjectRow(
                                    project,
                                    `recent-${projectIndex}`
                                )
                            )}

                            {/* Show divider with expandable section when client has both recent and non-recent jobs
                                This allows users to hide/show jobs that haven't been used recently */}
                            {notRecentJobs.length > 0 &&
                                recentJobs.length > 0 && (
                                    <>
                                        <JobsDivider
                                            isExpanded={isNotRecentExpanded}
                                            count={notRecentJobs.length}
                                            onClick={() =>
                                                toggleNotRecentJobs(
                                                    data.clientData.id
                                                )
                                            }
                                        />
                                        {isNotRecentExpanded &&
                                            notRecentJobs.map(
                                                (project, projectIndex) =>
                                                    renderProjectRow(
                                                        project,
                                                        `not-recent-${projectIndex}`
                                                    )
                                            )}
                                    </>
                                )}

                            {/* If all jobs are non-recent (IsUsedRecently = false), render them normally without divider
                                This prevents showing an unnecessary divider when there are no recent jobs to separate from */}
                            {notRecentJobs.length > 0 &&
                                recentJobs.length === 0 &&
                                notRecentJobs.map((project, projectIndex) =>
                                    renderProjectRow(
                                        project,
                                        `not-recent-${projectIndex}`
                                    )
                                )}
                        </VStack>
                    )}
                </VStack>
            </TimesheetGroupRow>
        </>
    );
};

Row.propTypes = {
    hoursData: PropTypes.object,
    data: PropTypes.object.isRequired,
    projectHours: PropTypes.array,
    oooReasonsData: PropTypes.array,
};

export default Row;
